import React, { useState, useEffect } from 'react';
import { content, logoUrl } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Globe, Phone, Mail, Calculator } from 'lucide-react';
import { Button } from './ui/button';
import PricingCalculator from './PricingCalculator';

const Navbar = () => {
  const { language, toggleLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const t = content[language]?.nav || content.en.nav;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: t.home, href: '#home' },
    { label: t.about, href: '#about' },
    { label: t.services, href: '#services' },
    { label: t.destinations, href: '#destinations' },
    { label: t.pricing, href: '#pricing' },
    { label: t.contact, href: '#contact' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  return (
    <>
      {/* Top bar */}
      <div className="hidden lg:block bg-slate-900 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a href="tel:+16130000000" className="flex items-center gap-2 hover:text-cyan-400 transition-colors">
              <Phone className="w-4 h-4" />
              +1 (613) 000-0000
            </a>
            <a href="mailto:info@transafricacanada.com" className="flex items-center gap-2 hover:text-cyan-400 transition-colors">
              <Mail className="w-4 h-4" />
              info@transafricacanada.com
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-400">Ottawa • Dakar • Kinshasa • Douala</span>
          </div>
        </div>
      </div>

      {/* Main navbar */}
      <nav className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg' 
          : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="#home" className="flex items-center gap-3">
              <img src={logoUrl} alt="TransAfrica Canada" className="h-14 w-auto" />
            </a>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <button
                  key={item.href}
                  onClick={() => scrollToSection(item.href)}
                  className="px-4 py-2 text-slate-700 hover:text-blue-600 font-medium transition-colors relative group"
                >
                  {item.label}
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-blue-600 transition-all duration-300 group-hover:w-3/4" />
                </button>
              ))}
            </div>

            {/* Right side */}
            <div className="hidden lg:flex items-center gap-4">
              {/* Language toggle */}
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <Globe className="w-5 h-5 text-slate-600" />
                <span className="font-medium text-slate-700">
                  {language === 'fr' ? 'EN' : 'FR'}
                </span>
              </button>

              {/* CTA Button */}
              <Button
                onClick={() => setShowCalculator(true)}
                className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-6"
              >
                <Calculator className="w-4 h-4 mr-2" />
                {t.getQuote}
              </Button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div className={`lg:hidden overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-screen' : 'max-h-0'
        }`}>
          <div className="px-4 py-4 bg-white border-t space-y-2">
            {navItems.map((item) => (
              <button
                key={item.href}
                onClick={() => scrollToSection(item.href)}
                className="block w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                {item.label}
              </button>
            ))}
            <div className="flex items-center gap-4 px-4 pt-4 border-t">
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <Globe className="w-5 h-5" />
                {language === 'fr' ? 'English' : 'Français'}
              </button>
            </div>
            <div className="px-4 pt-2">
              <Button
                onClick={() => { setShowCalculator(true); setIsOpen(false); }}
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white"
              >
                <Calculator className="w-4 h-4 mr-2" />
                {t.getQuote}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Pricing Calculator Modal */}
      <PricingCalculator 
        isOpen={showCalculator} 
        onClose={() => setShowCalculator(false)} 
      />
    </>
  );
};

export default Navbar;
